// src/pages/CoursePage.js
import React, { useRef, useEffect } from 'react';
import styled from 'styled-components';
import Plyr from 'plyr';
import 'plyr/dist/plyr.css'; // Import Plyr styles

// ================= Styled Components (same as before) =================
const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  overflow-x: hidden;

  @media (max-width: 768px) {
    padding: 20px 16px;
  }

  @media (max-width: 480px) {
    padding: 16px 12px;
  }
`;

const HeroSection = styled.div`
  text-align: center;
  padding: 80px 0 60px;
  background: linear-gradient(135deg, #f5f5f7 0%, #ffffff 50%, #f5f5f7 100%);
  border-radius: 24px;
  margin-bottom: 80px;
  position: relative;

  @media (max-width: 768px) {
    padding: 50px 0 40px;
    margin-bottom: 60px;
    border-radius: 20px;
  }

  @media (max-width: 480px) {
    padding: 40px 0 30px;
    margin-bottom: 40px;
    border-radius: 16px;
  }
`;

const HeroTitle = styled.h1`
  font-size: 64px;
  font-weight: 700;
  background: linear-gradient(45deg, #1d1d1f, #424245, #1d1d1f);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 24px;
  letter-spacing: -1px;
  line-height: 1.1;
  padding: 0 20px;

  @media (max-width: 1024px) {
    font-size: 56px;
  }

  @media (max-width: 768px) {
    font-size: 42px;
    margin-bottom: 20px;
    padding: 0 16px;
  }

  @media (max-width: 480px) {
    font-size: 32px;
    margin-bottom: 16px;
    padding: 0 12px;
    letter-spacing: -0.5px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 28px;
  color: #86868b;
  font-weight: 400;
  max-width: 800px;
  margin: 0 auto 40px;
  line-height: 1.4;
  padding: 0 30px;

  @media (max-width: 1024px) {
    font-size: 24px;
  }

  @media (max-width: 768px) {
    font-size: 20px;
    margin-bottom: 32px;
    padding: 0 20px;
  }

  @media (max-width: 480px) {
    font-size: 18px;
    margin-bottom: 24px;
    padding: 0 16px;
  }
`;

const SectionContainer = styled.div`
  margin-bottom: 60px;

  @media (max-width: 768px) {
    margin-bottom: 40px;
  }

  @media (max-width: 480px) {
    margin-bottom: 32px;
  }
`;

const SectionCard = styled.div`
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 40px;
  margin-bottom: 40px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
  }

  @media (max-width: 768px) {
    padding: 28px 24px;
    margin-bottom: 30px;
    border-radius: 20px;
  }

  @media (max-width: 480px) {
    padding: 20px 16px;
    margin-bottom: 24px;
    border-radius: 16px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 36px;
  font-weight: 700;
  color: #1d1d1f;
  margin-bottom: 16px;
  letter-spacing: -0.5px;

  @media (max-width: 768px) {
    font-size: 28px;
    margin-bottom: 12px;
  }

  @media (max-width: 480px) {
    font-size: 24px;
    margin-bottom: 10px;
  }
`;

const SectionDescription = styled.p`
  font-size: 18px;
  color: #515154;
  line-height: 1.5;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 24px;
  }

  @media (max-width: 480px) {
    font-size: 15px;
    margin-bottom: 20px;
  }
`;

// New styled wrapper for Plyr container
const PlyrWrapper = styled.div`
  position: relative;
  width: 100%;
  border-radius: 16px;
  overflow: hidden;
  background: #000;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  aspect-ratio: 16 / 9;

  .plyr {
    border-radius: 16px;
  }

  @media (max-width: 768px) {
    border-radius: 12px;
  }
`;

const VideoContainer = styled.div`
  position: relative;
  width: 100%;
`;

const SectionDivider = styled.hr`
  margin: 60px 0 20px;
  border: none;
  height: 2px;
  background: linear-gradient(90deg, transparent, #0071e3, transparent);
  opacity: 0.3;

  @media (max-width: 768px) {
    margin: 40px 0 20px;
  }
`;

const GlobalSectionTitle = styled.h2`
  font-size: 48px;
  font-weight: 600;
  text-align: center;
  background: linear-gradient(45deg, #1d1d1f, #424245);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 60px;
  letter-spacing: -0.5px;

  @media (max-width: 1024px) {
    font-size: 42px;
  }

  @media (max-width: 768px) {
    font-size: 36px;
    margin-bottom: 40px;
  }

  @media (max-width: 480px) {
    font-size: 32px;
    margin-bottom: 32px;
  }
`;

const ShieldNote = styled.div`
  text-align: center;
  margin-top: 60px;
  padding: 20px;
  background: rgba(0, 113, 227, 0.05);
  border-radius: 16px;
  color: #6e6e73;
  font-size: 14px;
  border: 1px solid rgba(0, 113, 227, 0.1);

  @media (max-width: 480px) {
    margin-top: 40px;
    padding: 16px;
    font-size: 12px;
  }
`;

// ================= Plyr Video Component =================
// This component uses Plyr to embed YouTube without any visible YouTube branding/links.
// The underlying iframe is still created by Plyr, but the custom UI replaces all controls.
const PlyrVideo = ({ videoId, title }) => {
  const containerRef = useRef(null);
  const playerRef = useRef(null);

  useEffect(() => {
    // Initialize Plyr only after the container is mounted
    if (!containerRef.current) return;

    // Plyr options – these control the YouTube player parameters and the custom UI
    const options = {
      // YouTube-specific parameters (passed to the underlying iframe)
      youtube: {
        noCookie: true,           // use youtube-nocookie.com
        rel: 0,                   // no related videos at end
        modestbranding: 1,        // reduce YouTube logo
        iv_load_policy: 3,        // disable annotations
        controls: 1,              // show controls (Plyr will still overlay its own)
        fs: 1,                    // allow fullscreen
        // showinfo is deprecated, but modestbranding and rel=0 already help
      },
      // Customize the Plyr control bar – omit any button that could link to YouTube
      controls: [
        'play-large',    // large play button
        'play',          // play/pause
        'progress',      // progress bar
        'current-time',  // current time
        'mute',          // mute
        'volume',        // volume slider
        'fullscreen',    // fullscreen toggle
        // 'settings',    // optional (no YouTube link)
        // 'pip',         // picture-in-picture
      ],
      // Disable the default context menu on the Plyr container (additional protection)
      tooltips: { controls: false },
      keyboard: { focused: true, global: false },
    };

    // Create the Plyr instance
    playerRef.current = new Plyr(containerRef.current, options);

    // Cleanup on unmount
    return () => {
      if (playerRef.current) {
        playerRef.current.destroy();
      }
    };
  }, [videoId]); // re-run if videoId changes (though it shouldn't in this component)

  // Prevent right-click on the entire video area (blocks any "Copy video URL" attempt)
  const handleContextMenu = (e) => {
    e.preventDefault();
    return false;
  };

  return (
    <VideoContainer onContextMenu={handleContextMenu}>
      <PlyrWrapper>
        {/* 
          Plyr requires a container with data-plyr-provider and data-plyr-embed-id.
          The actual YouTube URL is never written as text; Plyr constructs it internally.
        */}
        <div
          ref={containerRef}
          data-plyr-provider="youtube"
          data-plyr-embed-id={videoId}
          data-plyr-title={title}
        />
      </PlyrWrapper>
    </VideoContainer>
  );
};

// ================= Course Sections Data (unchanged) =================
const COURSE_SECTIONS = [
  {
    id: 1,
    title: "Transformer 101 (Background Info)",
    description: "",
    videoId: "bALaKmVANnY",
  },
  {
    id: 2,
    title: "Transformer Connections",
    description: "",
    videoId: "HNbAi9QwfoA",
  },
  {
    id: 3,
    title: "Nameplate",
    description: "",
    videoId: "Il1j7uja8y8",
  },
  {
    id: 4,
    title: "Internal Insulation",
    description: "",
    videoId: "N8Iydb6jcPI",
  },
  {
    id: 5,
    title: "External Insulation",
    description: "",
    videoId: "SHM21jlv2H0",
  },
  {
    id: 6,
    title: "Bushing Design Considerations",
    description: "",
    videoId: "I_U7rT-aZ9U",
  },
  {
    id: 7,
    title: "Bushing Standards",
    description: "",
    videoId: "c5VbWajqNus",
  },
  {
    id: 8,
    title: "Power Transformer: Basic",
    description: "",
    videoId: "2maUynWTP20",
  },
  {
    id: 9,
    title: "Power Transformer: Core",
    description: "",
    videoId: "v0HO1AsVGSg",
  },
  {
    id: 10,
    title: "Power Transformer: Winding",
    description: "",
    videoId: "uL6V7AAr6IQ",
  },
  {
    id: 11,
    title: "Power Transformer:Liquid Preservation System",
    description: "",
    videoId: "WiS9yrXjSpA",
  },
    {
    id: 12,
    title: "Power Transformer:Load Tap Changer",
    description: "",
    videoId: "kC5OU76RDZk",
  },
   {
    id: 13,
    title: "Power Transformer Protection",
    description: "",
    videoId: "zV8ijx3O9Vw",
  },
  {
    id: 14,
    title: "Distribution Transformer Basic",
    description: "",
    videoId: "g__p9VAWz8M",
  },
  {
    id: 15,
    title: "Distribution Transformer Types",
    description: "",
    videoId: "K_fWi8LYssE",
  },
  {
    id: 16,
    title: "Distribution Transformer Testing and Protection",
    description: "",
    videoId: "IcZluOnGTyw",
  },
  {
    id: 17,
    title: "Instrument Transformer",
    description: "",
    videoId: "p4jEgM8zjmI",
  },
  {
    id: 18,
    title: "Dry Type Transformer",
    description: "",
    videoId: "eKAPwRksj-I",
  },
  {
    id: 19,
    title: "Transformer Considerations: Constraints",
    description: "",
    videoId: "s-X_DMWQHdE",
  },
  {
    id: 18,
    title: "Transformer Considerations: Transformer Sound Levels",
    description: "",
    videoId: "k1-MVceFrxg",
  },
  {
    id: 18,
    title: "Transformer Considerations: Dry Type Transformer",
    description: "",
    videoId: "eKAPwRksj-I",
  },
  {
    id: 18,
    title: "Transformer Considerations: Inrush Current",
    description: "",
    videoId: "o9Z1_RI1pS8",
  },
  {
    id: 18,
    title: "Transformer Procurement",
    description: "",
    videoId: "O7QbGeME0lk",
  },
  {
    id: 18,
    title: "Transformer Installation",
    description: "",
    videoId: "FyFYZnSlY_g",
  },
  {
    id: 18,
    title: "Transformer Industry Standards",
    description: "",
    videoId: "cLeuRu3PLoQ",
  },
];

// ================= Main Component =================
const TxFundCourse = () => {
  return (
    <PageContainer>
      <HeroSection>
        <HeroTitle>Electrical Transformers Fundamentals</HeroTitle>
        <HeroSubtitle>
          Learn all about the practical industry knowledge of Electrical Transformers with this course!
        </HeroSubtitle>
      </HeroSection>

      <GlobalSectionTitle>📺 Course Modules (Video Lessons)</GlobalSectionTitle>

      {COURSE_SECTIONS.map((section, index) => (
        <SectionContainer key={section.id}>
          <SectionCard>
            <SectionTitle>{section.title}</SectionTitle>
            <SectionDescription>{section.description}</SectionDescription>
            {/* Replace the old ProtectedVideo with PlyrVideo */}
            <PlyrVideo videoId={section.videoId} title={section.title} />
          </SectionCard>
          {index < COURSE_SECTIONS.length - 1 && <SectionDivider />}
        </SectionContainer>
      ))}

      <ShieldNote>
        End of Course
        </ShieldNote>
    </PageContainer>
  );
};

export default TxFundCourse;